import { Box, HStack, Skeleton, Text, VStack } from '@chakra-ui/react'
import { EditableField } from '../EditableField'
import { DeleteButtonWithDialog } from './DeleteButtonWithDialog'
import { SkeletonTab, Tab } from './EditorTabs'
import { FC } from 'react'
import { EventTemplateWithImplementation } from '../../models/eventTemplateWithImplementation'
import { LearningEvent } from '../../auto-generated-clients/learning-management-client-v1.generated'
import { AddEventTemplateDialog } from './AddEventTemplateDialog'
import { v4 as uuid } from 'uuid'
import { ImplementationWithCompanyName } from '../../models/ImplementationWithCompanyName'
import { EditableTextArea } from './EditableTextArea'

interface EditorViewProps {
  selectedLearningEvent?: LearningEvent
  selectedEventTemplate: EventTemplateWithImplementation | undefined
  onEventClick: (event: EventTemplateWithImplementation) => void
  onDeleteLearningEvent: () => void
  onTitleEditSubmit: (value: string) => void
  eventTemplates: EventTemplateWithImplementation[]
  addEventTemplate: (implementationId: string) => void
  deleteEventTemplate: () => void
  updateEventTemplate: (updatedTemplate: string) => void
  isLearningEventLoading: boolean
  isEventTemplatesLoading: boolean
  implementations: ImplementationWithCompanyName[]
  setIsEditingEventTemplate: (isEditing: boolean) => void
}

const NUMBER_OF_LOADING_TABS = 3

export const EditorView: FC<EditorViewProps> = ({
  selectedLearningEvent,
  eventTemplates,
  onDeleteLearningEvent,
  onEventClick,
  onTitleEditSubmit,
  selectedEventTemplate,
  isLearningEventLoading,
  isEventTemplatesLoading,
  addEventTemplate,
  deleteEventTemplate,
  updateEventTemplate,
  implementations,
  setIsEditingEventTemplate,
}) => {
  const renderDetailsLoadingState = () => {
    return (
      <VStack width={'400px'}>
        <Skeleton width={'full'} height={'30px'} />
        <Skeleton width={'full'} height={'30px'} />
      </VStack>
    )
  }

  const renderDetails = () => {
    if (!selectedLearningEvent) {
      return <Box height={'73px'} />
    }
    return (
      <VStack>
        <EditableField
          text={selectedLearningEvent?.learningEventName ?? ''}
          onSubmit={onTitleEditSubmit}
        />
        <Text fontSize={'sm'} p={1}>
          Id: {selectedLearningEvent?.learningEventId}
        </Text>
      </VStack>
    )
  }

  const renderTabs = () => {
    if (isEventTemplatesLoading) {
      return Array.from({ length: NUMBER_OF_LOADING_TABS }).map(() => (
        <SkeletonTab key={uuid()} />
      ))
    }

    return eventTemplates.map((event) => {
      const isSelected =
        selectedEventTemplate?.eventTemplateId === event.eventTemplateId

      return (
        <Tab
          key={event.eventTemplateId}
          selected={isSelected}
          tabName={`${event.companyName} - ${event.implementationName}`}
          onClick={() => onEventClick(event)}
        />
      )
    })
  }

  if (!selectedLearningEvent) {
    return (
      <VStack
        bg={'secondary.0'}
        borderRadius={'xl'}
        p={4}
        boxSize={'full'}
        justifyContent={'center'}
        maxH={'calc(100vh - 330px)'}>
        <Text fontSize={'sm'} padding={4}>
          No Learning events found for this content, Create a new learning
          event!
        </Text>
      </VStack>
    )
  }

  return (
    <VStack
      bg={'secondary.0'}
      borderRadius={'xl'}
      p={4}
      boxSize={'full'}
      maxH={'calc(100vh - 330px)'}>
      <HStack
        width={'full'}
        alignItems={'flex-start'}
        justifyContent={'space-between'}>
        {isLearningEventLoading ? renderDetailsLoadingState() : renderDetails()}
        <DeleteButtonWithDialog
          onDelete={onDeleteLearningEvent}
          deletedObjectName={selectedLearningEvent?.learningEventName ?? ''}
        />
      </HStack>
      <HStack
        boxSize={'full'}
        alignItems={'flex-start'}
        maxHeight={'calc(100vh - 420px)'}>
        <EditableTextArea
          selectedEventTemplate={selectedEventTemplate}
          onSubmit={updateEventTemplate}
          deleteEventTemplate={deleteEventTemplate}
          setIsEditingEventTemplate={setIsEditingEventTemplate}
        />
        <VStack overflow={'auto'} height={'full'} maxH={'full'} width={'140px'}>
          <AddEventTemplateDialog
            implementations={implementations}
            addEventTemplate={addEventTemplate}
          />
          {renderTabs()}
        </VStack>
      </HStack>
    </VStack>
  )
}
